/**
 * ContentPreviewModal — preview de contenido antes de publicar.
 *
 * Recibe los mismos campos que se enviarán al backend (título / imagen /
 * resumen + content + content_blocks) y los renderiza usando los mismos
 * componentes públicos que vería un visitante. Útil para News y Events.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   title: string
 *   subtitle?: string    (resumen — solo News)
 *   imageUrl?: string
 *   contentHtml?: string (RichText legacy)
 *   contentBlocks?: array (RichContentBlocks)
 *   metaLine?: ReactNode (e.g. fecha + ubicación, para Events)
 */
import React, { useEffect } from 'react';
import { X, Eye, ExternalLink } from 'lucide-react';
import { absoluteUrl } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import RichContentBlocks from './RichContentBlocks';

export default function ContentPreviewModal({
  open, onClose, title, subtitle, imageUrl, contentHtml, contentBlocks, metaLine,
  previewToken, previewPath,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const publicUrl = previewToken && previewPath
    ? `${window.location.origin}${previewPath}/${previewToken}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-6"
      onClick={onClose}
      data-testid="content-preview-modal"
    >
      <div
        className="bg-white sm:rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col sm:max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between flex-shrink-0 gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-charcoal-700">
            <Eye size={16} className="text-cmic-600" />
            Vista previa — así se verá en el sitio
          </div>
          <div className="flex items-center gap-2">
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="content-preview-open-public"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1.5 rounded-md hover:bg-amber-100"
                title="Abre el enlace privado en una nueva pestaña — puedes compartirlo con consejeros"
              >
                <ExternalLink size={12} /> Abrir vista pública
              </a>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-charcoal-500" data-testid="content-preview-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 bg-gray-50">
          <article className="bg-white max-w-3xl mx-auto px-5 sm:px-10 py-8 sm:py-12 break-words">
            {imageUrl && (
              <div className="rounded-xl overflow-hidden mb-6 bg-gray-100">
                <img src={absoluteUrl(imageUrl)} alt={title} className="w-full h-auto max-h-[420px] object-cover" />
              </div>
            )}
            <h1 className="text-2xl sm:text-4xl font-bold text-charcoal-900 leading-tight break-words">{title || 'Sin título'}</h1>
            {metaLine && (
              <div className="mt-3 text-sm text-charcoal-500">{metaLine}</div>
            )}
            {subtitle && (
              <p className="mt-5 text-base text-charcoal-600 leading-relaxed italic border-l-3 border-cmic-200 pl-4 break-words">
                {subtitle}
              </p>
            )}
            {contentHtml && contentHtml.trim() && (
              <div
                className="prose prose-charcoal max-w-none prose-headings:text-charcoal-800 prose-a:text-cmic-600 prose-strong:text-charcoal-800 prose-img:rounded-xl prose-img:max-w-full mt-6 break-words"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(contentHtml) }}
              />
            )}
            <RichContentBlocks blocks={contentBlocks} />
            {!contentHtml?.trim() && (!Array.isArray(contentBlocks) || contentBlocks.length === 0) && !subtitle && (
              <p className="mt-6 text-sm text-charcoal-400 italic">
                (Aún no hay contenido en el cuerpo. Agrega texto principal o bloques adicionales para verlos aquí.)
              </p>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
